import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Clock, ArrowRight, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

export const Blog = () => {
  const [categories, setCategories] = useState(["All"]);
  const [active, setActive] = useState("All");
  const [search, setSearch] = useState("");
  const [articles, setArticles] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    // Non-critical: if categories fail, keep the default ["All"] so filtering still works.
    api.get("/categories").then((r) => setCategories(r.data)).catch((e) => {
      console.error("Journal: categories fetch failed, using default filter", e);
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    setLoadError(false);
    const params = {};
    if (active !== "All") params.category = active;
    if (search) params.search = search;
    const t = setTimeout(() => {
      api
        .get("/articles", { params })
        .then((r) => setArticles(r.data))
        .catch((e) => {
          // Degrade to an explicit error state (not a misleading "No articles found").
          console.error("Journal: articles fetch failed", e);
          setArticles([]);
          setLoadError(true);
        })
        .finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(t);
  }, [active, search, reloadKey]);

  const openArticle = async (slug) => {
    try {
      const r = await api.get(`/articles/${slug}`);
      setSelected(r.data);
    } catch (e) {
      // User-facing recovery: tell them and let them try again; the grid stays intact.
      console.error("Journal: failed to open article", slug, e);
      toast.error("We could not open that article just now. Please try again.");
    }
  };

  return (
    <section id="journal" className="py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <span className="eyebrow">Hair & beauty edit</span>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl text-stylist-ink">
              The Style Journal
            </h2>
          </div>
          <div className="relative w-full md:w-80">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-stylist-muted" />
            <input
              data-testid="blog-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search articles…"
              className="w-full pl-11 pr-4 py-3 rounded-full bg-white border border-stylist-border text-sm text-stylist-ink placeholder:text-stylist-muted focus:outline-none focus:border-stylist-gold transition-colors"
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-10" data-testid="blog-category-filter">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setActive(c)}
              data-testid={`blog-category-${c.replace(/\s|&/g, "").toLowerCase()}`}
              className={`px-4 py-2 rounded-full text-sm border transition-all duration-300 ${
                active === c
                  ? "bg-stylist-ink text-white border-stylist-ink"
                  : "bg-white text-stylist-ink2 border-stylist-border hover:border-stylist-gold hover:text-stylist-ink"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-20 text-stylist-muted font-mono text-sm">Loading…</div>
        ) : loadError ? (
          <div className="text-center py-20" data-testid="blog-load-error">
            <p className="font-serif text-2xl text-stylist-ink">We could not load the journal just now.</p>
            <p className="mt-2 text-sm text-stylist-ink2">Please check your connection and try again.</p>
            <button
              onClick={() => setReloadKey((k) => k + 1)}
              data-testid="blog-retry-button"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-stylist-ink text-white px-6 py-3 text-sm hover:bg-stylist-gold transition-colors"
            >
              Try again
            </button>
          </div>
        ) : articles.length === 0 ? (
          <div className="text-center py-20 text-stylist-muted font-serif text-2xl">
            No articles found.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {articles.map((a, i) => (
              <motion.article
                key={a.slug}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
                onClick={() => openArticle(a.slug)}
                data-testid="blog-card-item"
                className="group cursor-pointer bg-white border border-stylist-border rounded-lg overflow-hidden hover:shadow-2xl hover:-translate-y-1 hover:border-stylist-gold/40 transition-all duration-500"
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={a.image}
                    alt={a.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <span className="absolute top-4 left-4 text-xs font-mono uppercase tracking-widest bg-white/90 text-stylist-ink px-3 py-1.5 rounded-full">
                    {a.category}
                  </span>
                </div>
                <div className="p-6">
                  <h3 className="font-serif text-xl md:text-2xl text-stylist-ink leading-snug group-hover:text-stylist-gold transition-colors">
                    {a.title}
                  </h3>
                  <p className="mt-3 text-sm text-stylist-ink2 leading-relaxed line-clamp-3">
                    {a.excerpt}
                  </p>
                  <div className="mt-5 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <img src={a.author_avatar} alt={a.author} className="w-7 h-7 rounded-full object-cover" />
                      <span className="text-xs text-stylist-ink2">{a.author}</span>
                    </div>
                    <span className="flex items-center gap-1.5 text-xs text-stylist-muted font-mono">
                      <Clock className="w-3.5 h-3.5" />
                      {a.read_time}
                    </span>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        )}
      </div>

      <ArticleModal article={selected} onClose={() => setSelected(null)} onOpen={openArticle} />
    </section>
  );
};

const ArticleModal = ({ article, onClose }) => {
  useEffect(() => {
    if (article) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => (document.body.style.overflow = "");
  }, [article]);

  return (
    <AnimatePresence>
      {article && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 z-[60] bg-stylist-ink/70 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4 md:p-10"
          data-testid="blog-detail-modal"
        >
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 40 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative bg-[#FAF8F5] rounded-lg max-w-3xl w-full mb-10 overflow-hidden"
          >
            <button
              onClick={onClose}
              data-testid="blog-modal-close"
              className="absolute top-4 right-4 z-10 p-2.5 rounded-full bg-white/90 hover:bg-white text-stylist-ink shadow-md"
            >
              <X className="w-5 h-5" />
            </button>
            <img src={article.image} alt={article.title} className="w-full h-64 md:h-80 object-cover" />
            <div className="p-6 md:p-12">
              <span className="eyebrow">{article.category}</span>
              <h1 className="mt-3 font-serif text-3xl md:text-4xl leading-tight text-stylist-ink">
                {article.title}
              </h1>
              <div className="mt-5 flex items-center gap-4 pb-8 border-b border-stylist-border">
                <img src={article.author_avatar} alt={article.author} className="w-11 h-11 rounded-full object-cover" />
                <div>
                  <p className="text-sm font-medium text-stylist-ink">{article.author}</p>
                  <p className="text-xs text-stylist-muted font-mono">
                    {article.date} · {article.read_time}
                  </p>
                </div>
              </div>

              <div className="mt-8 space-y-5">
                {article.body.map((p, i) => (
                  <p key={i} className="text-[17px] leading-[1.8] text-stylist-ink2">
                    {p}
                  </p>
                ))}
              </div>

              {article.quote && (
                <blockquote className="my-10 border-l-2 border-stylist-gold pl-6 py-2">
                  <p className="font-serif text-2xl md:text-3xl italic text-stylist-ink leading-snug">
                    “{article.quote}”
                  </p>
                </blockquote>
              )}

              <div className="mt-8 flex items-center gap-2 text-sm text-stylist-gold">
                Want personalised advice? Get the app to consult a stylist
                <ArrowRight className="w-4 h-4" />
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
